import { GridTableApi } from '@api/GridTable.api';
import { GridTable, GridTableColumn, GridTableView } from '@api/models/gridTableApi.models';
import { ExternshipTrackerView } from '@common/fetches/useFetchGridTableByType';
import { SelectChangeEvent, Box, FormControl, Select, MenuItem, Typography, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, Button, Tooltip } from '@mui/material';
import { GridFilterModel, GridLogicOperator } from '@mui/x-data-grid-pro';
import React from 'react';
import { SaveGridTableViewModal } from './SaveGridTableViewModal';
import { useToast } from '@components/Toast';
import DeleteIcon from '@mui/icons-material/Delete';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';

// GridTableViewSelector component for selecting, managing, and saving views
export const GridTableViewSelector: React.FC<{
  gridTable: GridTable | undefined;
  currView: ExternshipTrackerView | undefined;
  setCurrView: React.Dispatch<React.SetStateAction<ExternshipTrackerView | undefined>>;
  setGridTable: React.Dispatch<React.SetStateAction<GridTable | undefined>>;
  filterModel: GridFilterModel | undefined;
  columnVisibilityModel: Record<string, boolean> | undefined;
  customColumnOrder: number[];
  sortGridTableColumnInCustomOrder: (columnIds: number[]) => GridTableColumn[];
  onOpenSaveViewModal: () => void;
}> = ({
  gridTable,
  currView,
  setCurrView,
  setGridTable,
  filterModel,
  columnVisibilityModel,
  customColumnOrder,
  onOpenSaveViewModal
}) => {
  const { openToast } = useToast();
  const [viewDialogOpen, setViewDialogOpen] = React.useState(false);
  const [viewToRename, setViewToRename] = React.useState<GridTableView>();
  const [confirmDeleteOpen, setConfirmDeleteOpen] = React.useState(false);
  const [viewToDelete, setViewToDelete] = React.useState<GridTableView>();

  const handleViewChange = (event: SelectChangeEvent<string | number>): void => {
    const viewIdStr = event.target.value.toString();
    const viewId = parseInt(viewIdStr);

    // Find view in default or custom views
    const view =
        gridTable?.defaultViews.find((v) => v.id === viewId) ||
        gridTable?.customViews.find((v) => v.id === viewId);

    if (view) {
      setCurrView({ currView: view.id });
    }
  };

  const handleDeleteView = async (): Promise<void> => {
    if (!gridTable || !viewToDelete) return;

    try {
      await GridTableApi.deleteGridTableView(gridTable.id, viewToDelete.id);

      setGridTable({
        ...gridTable,
        customViews: gridTable.customViews.filter((v) => v.id !== viewToDelete.id)
      });

      // If deleted view was current view, switch to default view
      if (currView?.currView === viewToDelete.id) {
        setCurrView({ currView: gridTable.defaultViewId });
      }

      openToast('View deleted successfully');
    } catch (error) {
      console.error('Error deleting view', error);
      openToast('Error deleting view. Please try again.');
    } finally {
      setConfirmDeleteOpen(false);
      setViewToDelete(undefined);
    }
  };

  const handleSetDefaultView = async (view: GridTableView): Promise<void> => {
    if (!gridTable) return;

    try {
      await GridTableApi.setDefaultGridTableView(gridTable.id, view.id);

      setGridTable({
        ...gridTable,
        defaultViewId: view.id
      });

      openToast('Default view set successfully');
    } catch (error) {
      console.error('Error setting default view', error);
      openToast('Error setting default view. Please try again.');
    }
  };

  const handleOpenRenameDialog = (view: GridTableView): void => {
    setViewToRename(view);
    setViewDialogOpen(true);
  };

  const handleOpenDeleteDialog = (view: GridTableView): void => {
    setViewToDelete(view);
    setConfirmDeleteOpen(true);
  };

  const allViews = React.useMemo(() => {
    if (!gridTable) return [];
    return [...gridTable.defaultViews, ...gridTable.customViews];
  }, [gridTable]);

  // Make sure we have a valid value for the select component
  const currentViewValue = React.useMemo<number | undefined>(() => {
    // Check for currView first
    if (currView?.currView !== undefined) {
      return currView.currView;
    }
    // Fall back to default view from grid table
    if (gridTable?.defaultViewId !== undefined) {
      return gridTable.defaultViewId;
    }
    // Return undefined for initial state
    return undefined;
  }, [currView, gridTable]);

  return (
    <>
      <Box display="flex" alignItems="center" gap={0.5}>
        <FormControl sx={{ minWidth: 200 }}>
          <Select<number | string>
            value={currentViewValue ?? ''}
            onChange={handleViewChange}
            displayEmpty
            sx={{ height: '45px' }}
          >
            {allViews.map((view) => (
              <MenuItem key={view.id} value={view.id}>
                <Box display="flex" alignItems="center" width="100%" justifyContent="space-between">
                  <Typography color="text.secondary">{view.name}</Typography>
                  {view.id === gridTable?.defaultViewId && (
                    <StarIcon fontSize="small" color="primary" sx={{ ml: 1 }} />
                  )}
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Tooltip title={`Save Current View${!gridTable ? ' disabled: (Grid Table not loaded)' : ''}`}>
          <span style={{ display: 'inline-block' }}>
            <IconButton onClick={onOpenSaveViewModal} size="small" disabled={!gridTable}>
              <SaveIcon />
            </IconButton>
          </span>
        </Tooltip>

        {currView && gridTable && (
          <>
            {gridTable.customViews.some(v => v.id === currView.currView) && (
              <>
                <Tooltip title="Rename View">
                  <IconButton
                    onClick={() => {
                      const view = gridTable.customViews.find(v => v.id === currView.currView);
                      if (view) handleOpenRenameDialog(view);
                    }}
                    size="small"
                  >
                    <EditIcon />
                  </IconButton>
                </Tooltip>

                <Tooltip title="Delete View">
                  <IconButton
                    onClick={() => {
                      const view = gridTable.customViews.find(v => v.id === currView.currView);
                      if (view) handleOpenDeleteDialog(view);
                    }}
                    size="small"
                  >
                    <DeleteIcon />
                  </IconButton>
                </Tooltip>
              </>
            )}

            <Tooltip title="Set as Default View">
              <span style={{ display: 'inline-block' }}>
                <IconButton
                  onClick={() => {
                    const view = allViews.find(v => v.id === currView.currView);
                    if (view) handleSetDefaultView(view);
                  }}
                  size="small"
                  disabled={currView.currView === gridTable.defaultViewId}
                >
                  {currView.currView === gridTable.defaultViewId ?
                    <StarIcon color="primary" /> :
                    <StarBorderIcon />}
                </IconButton>
              </span>
            </Tooltip>
          </>
        )}
      </Box>

      <SaveGridTableViewModal
        open={viewDialogOpen}
        handleClose={() => {
          setViewDialogOpen(false);
          setViewToRename(undefined);
        }}
        gridTable={gridTable}
        columns={[]}
        viewToRename={viewToRename}
        getFilterColumns={() => ({
          items: filterModel?.items?.map((item, index) => ({
            field: item.field,
            operator: item.operator,
            value: Array.isArray(item.value) ? item.value.join(',') : item.value?.toString() || '',
            columnId: gridTable?.columns.find(col => col.field === item.field)?.id || 0,
            ordinalNumber: index + 1
          })),
          logicOperator: filterModel?.logicOperator || GridLogicOperator.And
        })}
        getSaveGridTableViewColumns={() =>
          gridTable?.columns.map((column, index) => ({
            columnId: column.id,
            isVisible: columnVisibilityModel?.[column.field] !== false,
            ordinalNumber: customColumnOrder.indexOf(column.id) > -1
              ? customColumnOrder.indexOf(column.id) + 1
              : index + 1
          })) || []
        }
        setGridTable={setGridTable}
        setCurrView={setCurrView}
      />

      <Dialog
        open={confirmDeleteOpen}
        onClose={() => setConfirmDeleteOpen(false)}
        aria-labelledby="delete-dialog-title"
      >
        <DialogTitle id="delete-dialog-title">Delete View</DialogTitle>
        <DialogContent>
          <Typography>
              Are you sure you want to delete the view &quot;{viewToDelete?.name}&quot;?
              This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDeleteOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteView} color="error" variant="contained">
              Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};